import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@nfp-tracker/components";
import {
  PRIVACY_POLICY_URL,
  STRIPE_DEFAULT_TRIAL_DAYS,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_URL,
  TERMS_OF_USE_URL,
} from "@nfp-tracker/utils";
import { useSupportChatPanel } from "../components/SupportChatContext";
import { useSubscriptionStorefrontAccess } from "../hooks/useSubscriptionManagementAvailability";

const Help = () => {
  const { openSupportChat } = useSupportChatPanel();
  const trialDayLabel = `${STRIPE_DEFAULT_TRIAL_DAYS}-day`;
  const termsUrl = (import.meta.env.VITE_TERMS_URL as string | undefined) ?? TERMS_OF_USE_URL;
  const privacyUrl = (import.meta.env.VITE_PRIVACY_URL as string | undefined) ?? PRIVACY_POLICY_URL;
  const { isSubscriptionManagementAvailable } =
    useSubscriptionStorefrontAccess();

  return (
    <div className="space-y-6">
      <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>How do I track my observations?</AccordionTrigger>
                  <AccordionContent>
                    Navigate to the "Observations" page from your dashboard. You'll find simple forms to record:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Bleeding intensity (1-3)</li>
                      <li>Monitor readings (Low, High, Peak) or Mucus observations</li>
                      <li>Intercourse</li>
                      <li>Symptoms and additional notes</li>
                    </ul>
                    <p className="mt-2">Save your observations daily for accurate cycle tracking.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>What do the cycle status labels mean?</AccordionTrigger>
                  <AccordionContent>
                    The app shows simple status labels based on your selected method and the observations you record:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li><strong>Lower fertility estimate:</strong> Logged observations currently look less likely to reflect a fertile window</li>
                      <li><strong>Higher fertility estimate:</strong> Logged observations currently look more consistent with a fertile window</li>
                      <li><strong>Menstrual tracking / pregnancy mode:</strong> Informational labels used for those specific contexts</li>
                    </ul>
                    <p className="mt-2">
                      These labels are educational summaries of your own entries, not medical recommendations or diagnoses.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>How do I invite my spouse?</AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal pl-6 space-y-2">
                      <li>Go to Settings</li>
                      <li>Find the "Spouse Management" section</li>
                      <li>Enter your spouse's email address</li>
                      <li>Click "Send" to send an invitation</li>
                    </ol>
                    <p className="mt-2">Your spouse will receive an email with a link to join your shared account. Once they accept, you'll both have access to the same cycle data.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>What NFP methods are supported?</AccordionTrigger>
                  <AccordionContent>
                    We support multiple Natural Family Planning methods:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li><strong>Marquette Method:</strong> Uses hormone monitor readings (Low, High, Peak)</li>
                      <li><strong>Creighton Model:</strong> Focuses on detailed mucus observation tracking</li>
                      <li><strong>Sympto-Thermal:</strong> Combines multiple signs of fertility</li>
                      <li><strong>Billings Method:</strong> Mucus-only observation method</li>
                      <li><strong>Other:</strong> Custom methods you can specify</li>
                    </ul>
                    <p className="mt-2">Select your method during onboarding or change it in Settings.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>How do I export my data?</AccordionTrigger>
                  <AccordionContent>
                    You can export your data in two formats:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li><strong>CSV Export:</strong> Go to Settings → Export Data → CSV. Perfect for spreadsheet analysis.</li>
                      <li><strong>PDF Report:</strong> Available on Calendar, Dashboard, and Cycle History pages. Creates a formatted report of your cycles and observations.</li>
                    </ul>
                    <p className="mt-2">You can optionally filter by date range before exporting.</p>
                  </AccordionContent>
                </AccordionItem>

                {isSubscriptionManagementAvailable && (
                  <AccordionItem value="item-6">
                    <AccordionTrigger>{`What happens during the ${trialDayLabel} free trial?`}</AccordionTrigger>
                    <AccordionContent>
                      {`When a ${trialDayLabel} introductory offer is available for your selected subscription, you can use it to start with full access:`}
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Full access to all tracking features</li>
                        <li>Apple or Stripe will show the exact billing terms at checkout</li>
                        <li>Spouse invitation and sharing capabilities</li>
                        <li>All export and notification features</li>
                      </ul>
                      <p className="mt-2">After the introductory period, your selected subscription continues automatically unless it is canceled. Your data is never deleted.</p>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {isSubscriptionManagementAvailable && (
                  <AccordionItem value="item-7">
                    <AccordionTrigger>How do I cancel my subscription?</AccordionTrigger>
                    <AccordionContent>
                      To manage or cancel your subscription:
                      <ol className="list-decimal pl-6 space-y-2">
                        <li>Go to Settings → Subscription → Manage</li>
                        <li>Or visit the Subscription page directly</li>
                        <li>Click "Manage Subscription" if you're an active subscriber</li>
                        <li>iPhone app purchases are managed in your App Store subscriptions settings</li>
                        <li>Web subscriptions are managed in our secure billing portal</li>
                      </ol>
                      <p className="mt-2">Your data remains accessible until the end of your billing period.</p>
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="item-8">
                  <AccordionTrigger>Is my data private and secure?</AccordionTrigger>
                  <AccordionContent>
                    We take your privacy seriously. Here's how it works:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>All data is encrypted and stored securely</li>
                      <li>
                        Your chart is visible to you, a spouse you invite, and any NFP instructor you
                        invite (instructors can view but not edit)
                      </li>
                      <li>
                        We use service providers to run the app, and they process data on our behalf:
                        hosting and database (Supabase), product analytics and error tracking (PostHog,
                        Sentry), email (Resend), and payments (Stripe, Apple)
                      </li>
                      <li>We don't sell your data or use it for advertising</li>
                      <li>You can export or delete your data anytime</li>
                    </ul>
                    <p className="mt-2">
                      Read our
                      {" "}
                      {privacyUrl ? (
                        <a
                          href={privacyUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          Privacy Policy
                        </a>
                      ) : (
                        <span>Privacy Policy</span>
                      )}
                      {" "}
                      for complete details.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9">
                  <AccordionTrigger>How should I interpret the tracking summaries?</AccordionTrigger>
                  <AccordionContent>
                    The tracking summaries use your selected method and the information you log in the app. Keep in mind:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>This app is for <strong>tracking and educational reference</strong></li>
                      <li>Summaries are only as complete as the observations you enter</li>
                      <li>The app does <strong>not</strong> diagnose, treat, or replace professional care</li>
                      <li>Consult your instructor or clinician to interpret patterns and make care decisions</li>
                    </ul>
                    <p className="mt-2">
                      For source material, use the reference links above or your method&apos;s official resources.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10">
                  <AccordionTrigger>Need more help?</AccordionTrigger>
                  <AccordionContent>
                    If you can't find the answer you're looking for:
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Email us at <a href={SUPPORT_MAILTO_URL} className="text-primary hover:underline">{SUPPORT_EMAIL}</a></li>
                      <li>We typically respond within 24 hours</li>
                      <li>
                        Check our
                        {" "}
                        {termsUrl ? (
                          <a
                            href={termsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            Terms of Service
                          </a>
                        ) : (
                          <span>Terms of Service</span>
                        )}
                        {" "}
                        for account policies
                      </li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2 text-center">
            <p className="text-sm text-muted-foreground">
              Still have questions? We're here to help!
            </p>
            <Button onClick={openSupportChat}>Contact Support</Button>
            <p className="text-xs text-muted-foreground">
              Prefer email? Write to{" "}
              <a className="underline" href={SUPPORT_MAILTO_URL}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Help;
